/* A small pooled particle system in logical scene pixels. */

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
  gravity: number;
  shape: 'square' | 'heart' | 'spark';
}

const HEART = ['.#.#.', '#####', '#####', '.###.', '..#..'];

export class Particles {
  private readonly items: Particle[] = [];

  spawn(p: Partial<Particle> & Pick<Particle, 'x' | 'y'>): void {
    if (this.items.length > 400) this.items.shift();
    this.items.push({
      vx: 0,
      vy: 0,
      life: 1,
      maxLife: 1,
      size: 1,
      color: '#ffffff',
      gravity: 0,
      shape: 'square',
      ...p,
    });
  }

  burst(
    x: number,
    y: number,
    count: number,
    opts: {
      colors: readonly string[];
      speed?: number;
      life?: number;
      gravity?: number;
      size?: number;
      shape?: Particle['shape'];
      up?: boolean;
    },
  ): void {
    for (let i = 0; i < count; i++) {
      const a = opts.up ? -Math.PI / 2 + (Math.random() - 0.5) * 1.6 : Math.random() * Math.PI * 2;
      const s = (opts.speed ?? 40) * (0.4 + Math.random() * 0.8);
      const life = (opts.life ?? 0.8) * (0.6 + Math.random() * 0.6);
      this.spawn({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life,
        maxLife: life,
        size: opts.size ?? 1,
        gravity: opts.gravity ?? 60,
        color: opts.colors[i % opts.colors.length] ?? '#fff',
        shape: opts.shape ?? 'square',
      });
    }
  }

  update(dt: number): void {
    for (let i = this.items.length - 1; i >= 0; i--) {
      const p = this.items[i]!;
      p.life -= dt;
      if (p.life <= 0) {
        this.items.splice(i, 1);
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    for (const p of this.items) {
      const alpha = Math.min(1, p.life / (p.maxLife * 0.5));
      ctx.globalAlpha = alpha;
      ctx.fillStyle = p.color;
      const x = Math.round(p.x);
      const y = Math.round(p.y);
      if (p.shape === 'heart') {
        HEART.forEach((row, yy) => {
          for (let xx = 0; xx < row.length; xx++)
            if (row[xx] === '#') ctx.fillRect(x + xx - 2, y + yy - 2, 1, 1);
        });
      } else if (p.shape === 'spark') {
        ctx.fillRect(x, y - 1, 1, 3);
        ctx.fillRect(x - 1, y, 3, 1);
      } else {
        ctx.fillRect(x, y, p.size, p.size);
      }
    }
    ctx.globalAlpha = 1;
  }

  clear(): void {
    this.items.length = 0;
  }
}
